/**
 * Eager injection of the PrimeVue theme CSS for every component and directive
 * the app uses.
 *
 * PrimeVue (4.5) injects a component's theme `<style>` tags from the FIRST
 * instance of that component to reach `beforeMount`, in two steps: it marks the
 * component as loaded in its theme registry, then defers the actual injection
 * to that instance's `mounted` hook (`useStyle` → `tryOnMounted`). Vue 3.5
 * disposes the pending `mounted` hooks of an instance unmounted before the
 * scheduler's post-flush runs (`invalidateMount`). So a first instance created
 * and torn down inside one flush — a parent re-rendering during its own mount,
 * which the Overview screen does — leaves the registry saying "loaded" with
 * nothing in `<head>`: every later instance renders without its theme, for the
 * whole session. Observed 2026-09-14 on the Popover: the "Ré-évaluer cette
 * exigence" confirmation had a transparent background, the page text showing
 * through. Still present in the latest published PrimeVue.
 *
 * Injecting the CSS here, synchronously and outside any component instance
 * (`tryOnMounted` runs the load immediately when there is no current instance),
 * makes the later per-instance load a no-op — `useStyle` finds the tag by name
 * and keeps it — so there is nothing left for the race to lose. Preloading
 * EVERY entry the app uses, not just the one that bit, is deliberate: the race
 * is generic, and which component is first in a doomed subtree is a property of
 * the screen, not of the component.
 *
 * Call after `app.use(PrimeVue, { theme })`: the theme getters read the theme
 * installed there.
 *
 * MAINTENANCE — the list below mirrors the PrimeVue entries used under `src/`
 * (explicit `from 'primevue/x'` imports and auto-imported tags). Using a
 * PrimeVue component or directive for the first time in the app means adding
 * its `primevue/x/style` here; `primevueThemePreload.test.ts` derives the used
 * set from the sources and fails on a used-but-not-preloaded entry.
 */
import type { BaseStyle } from '@primevue/core/base/style'

import FormStyle from '@primevue/forms/form/style'
import AccordionStyle from 'primevue/accordion/style'
import AccordionContentStyle from 'primevue/accordioncontent/style'
import AccordionHeaderStyle from 'primevue/accordionheader/style'
import AccordionPanelStyle from 'primevue/accordionpanel/style'
import AutoCompleteStyle from 'primevue/autocomplete/style'
import AvatarStyle from 'primevue/avatar/style'
import BadgeStyle from 'primevue/badge/style'
import ButtonStyle from 'primevue/button/style'
import CardStyle from 'primevue/card/style'
import CarouselStyle from 'primevue/carousel/style'
import CheckboxStyle from 'primevue/checkbox/style'
import ChipStyle from 'primevue/chip/style'
import ColumnStyle from 'primevue/column/style'
import ConfirmDialogStyle from 'primevue/confirmdialog/style'
import ConfirmPopupStyle from 'primevue/confirmpopup/style'
import DataTableStyle from 'primevue/datatable/style'
import DataViewStyle from 'primevue/dataview/style'
import DatePickerStyle from 'primevue/datepicker/style'
import DialogStyle from 'primevue/dialog/style'
import DividerStyle from 'primevue/divider/style'
import DrawerStyle from 'primevue/drawer/style'
import DropdownStyle from 'primevue/dropdown/style'
import FieldsetStyle from 'primevue/fieldset/style'
import IconFieldStyle from 'primevue/iconfield/style'
import InlineMessageStyle from 'primevue/inlinemessage/style'
import InputIconStyle from 'primevue/inputicon/style'
import InputNumberStyle from 'primevue/inputnumber/style'
import InputSwitchStyle from 'primevue/inputswitch/style'
import InputTextStyle from 'primevue/inputtext/style'
import KnobStyle from 'primevue/knob/style'
import ListboxStyle from 'primevue/listbox/style'
import MenuStyle from 'primevue/menu/style'
import MenubarStyle from 'primevue/menubar/style'
import MessageStyle from 'primevue/message/style'
import MultiSelectStyle from 'primevue/multiselect/style'
import PaginatorStyle from 'primevue/paginator/style'
import PanelStyle from 'primevue/panel/style'
import PasswordStyle from 'primevue/password/style'
import PopoverStyle from 'primevue/popover/style'
import ProgressBarStyle from 'primevue/progressbar/style'
import ProgressSpinnerStyle from 'primevue/progressspinner/style'
import RadioButtonStyle from 'primevue/radiobutton/style'
import RatingStyle from 'primevue/rating/style'
import RippleStyle from 'primevue/ripple/style'
import SelectStyle from 'primevue/select/style'
import SelectButtonStyle from 'primevue/selectbutton/style'
import SkeletonStyle from 'primevue/skeleton/style'
import SplitterStyle from 'primevue/splitter/style'
import SplitterPanelStyle from 'primevue/splitterpanel/style'
import StepStyle from 'primevue/step/style'
import StepListStyle from 'primevue/steplist/style'
import StepPanelStyle from 'primevue/steppanel/style'
import StepPanelsStyle from 'primevue/steppanels/style'
import StepperStyle from 'primevue/stepper/style'
import TabStyle from 'primevue/tab/style'
import TabListStyle from 'primevue/tablist/style'
import TabPanelStyle from 'primevue/tabpanel/style'
import TabPanelsStyle from 'primevue/tabpanels/style'
import TabsStyle from 'primevue/tabs/style'
import TabViewStyle from 'primevue/tabview/style'
import TagStyle from 'primevue/tag/style'
import TextareaStyle from 'primevue/textarea/style'
import ToastStyle from 'primevue/toast/style'
import ToggleSwitchStyle from 'primevue/toggleswitch/style'
import TooltipStyle from 'primevue/tooltip/style'
import TreeStyle from 'primevue/tree/style'

/** The runtime surface of `BaseStyle` this needs. These members exist on every
 *  component style object (`@primevue/core/base/style/index.mjs`) but are absent
 *  from the shipped `index.d.ts`, hence the local widening. */
interface ThemeStyle extends BaseStyle {
  name: string
  getComponentTheme: () => { css?: string; style?: string } | undefined
  getDirectiveTheme: () => { css?: string; style?: string } | undefined
  loadStyle: (options: { name: string }, style?: string) => unknown
}

/** One entry per PrimeVue package path used under `src/`, keyed by that path so
 *  the test can diff it against the sources. Order is irrelevant: PrimeVue's own
 *  order is "whichever mounts first", so no theme rule may depend on it. */
export const PRELOADED_PRIMEVUE_STYLES: Record<string, unknown> = {
  '@primevue/forms/form': FormStyle,
  'primevue/accordion': AccordionStyle,
  'primevue/accordioncontent': AccordionContentStyle,
  'primevue/accordionheader': AccordionHeaderStyle,
  'primevue/accordionpanel': AccordionPanelStyle,
  'primevue/autocomplete': AutoCompleteStyle,
  'primevue/avatar': AvatarStyle,
  'primevue/badge': BadgeStyle,
  'primevue/button': ButtonStyle,
  'primevue/card': CardStyle,
  'primevue/carousel': CarouselStyle,
  'primevue/checkbox': CheckboxStyle,
  'primevue/chip': ChipStyle,
  'primevue/column': ColumnStyle,
  'primevue/confirmdialog': ConfirmDialogStyle,
  'primevue/confirmpopup': ConfirmPopupStyle,
  'primevue/datatable': DataTableStyle,
  'primevue/dataview': DataViewStyle,
  'primevue/datepicker': DatePickerStyle,
  'primevue/dialog': DialogStyle,
  'primevue/divider': DividerStyle,
  'primevue/drawer': DrawerStyle,
  'primevue/dropdown': DropdownStyle,
  'primevue/fieldset': FieldsetStyle,
  'primevue/iconfield': IconFieldStyle,
  'primevue/inlinemessage': InlineMessageStyle,
  'primevue/inputicon': InputIconStyle,
  'primevue/inputnumber': InputNumberStyle,
  'primevue/inputswitch': InputSwitchStyle,
  'primevue/inputtext': InputTextStyle,
  'primevue/knob': KnobStyle,
  'primevue/listbox': ListboxStyle,
  'primevue/menu': MenuStyle,
  'primevue/menubar': MenubarStyle,
  'primevue/message': MessageStyle,
  'primevue/multiselect': MultiSelectStyle,
  'primevue/paginator': PaginatorStyle,
  'primevue/panel': PanelStyle,
  'primevue/password': PasswordStyle,
  'primevue/popover': PopoverStyle,
  'primevue/progressbar': ProgressBarStyle,
  'primevue/progressspinner': ProgressSpinnerStyle,
  'primevue/radiobutton': RadioButtonStyle,
  'primevue/rating': RatingStyle,
  'primevue/ripple': RippleStyle,
  'primevue/select': SelectStyle,
  'primevue/selectbutton': SelectButtonStyle,
  'primevue/skeleton': SkeletonStyle,
  'primevue/splitter': SplitterStyle,
  'primevue/splitterpanel': SplitterPanelStyle,
  'primevue/step': StepStyle,
  'primevue/steplist': StepListStyle,
  'primevue/steppanel': StepPanelStyle,
  'primevue/steppanels': StepPanelsStyle,
  'primevue/stepper': StepperStyle,
  'primevue/tab': TabStyle,
  'primevue/tablist': TabListStyle,
  'primevue/tabpanel': TabPanelStyle,
  'primevue/tabpanels': TabPanelsStyle,
  'primevue/tabs': TabsStyle,
  'primevue/tabview': TabViewStyle,
  'primevue/tag': TagStyle,
  'primevue/textarea': TextareaStyle,
  'primevue/toast': ToastStyle,
  'primevue/toggleswitch': ToggleSwitchStyle,
  'primevue/tooltip': TooltipStyle,
  'primevue/tree': TreeStyle,
}

/** Mirrors `BaseComponent._loadThemeStyles` (and `BaseDirective`'s twin for the
 *  `*-directive` styles) for one entry: the token variables tag, then the rules
 *  tag, under the names PrimeVue itself uses. */
function preloadTheme(style: ThemeStyle): void {
  const theme = style.name.endsWith('-directive')
    ? style.getDirectiveTheme()
    : style.getComponentTheme()
  const { css, style: extraCss } = theme ?? {}
  style.load?.(css ?? '', { name: `${style.name}-variables` })
  style.loadStyle({ name: `${style.name}-style` }, extraCss)
}

export function preloadPrimeVueComponentThemes(): void {
  Object.values(PRELOADED_PRIMEVUE_STYLES).forEach((style) => preloadTheme(style as ThemeStyle))
}
